import { useEffect, useRef, useState } from 'react';

import SimpleSelect from '@/components/atoms/SimpleSelect';
import SimpleTextField from '@/components/atoms/SimpleTextField';
import {
	AbsenteeismCategory,
	AbsenteeismCategoryType,
	ExtraTripMotive,
	ExtraTripMotiveType,
} from '@/constants/ExtraTripConstants';

interface ExtraTripFormFieldsProps {
	formValues: {
		motive: ExtraTripMotiveType;
		detail: string;
		category: AbsenteeismCategoryType;
		coveredEmployee: string | number | null;
		coveredEmployeeStructure: string;
		coveredEmployeeTeam: string;
		vacancy: string;
		team: string | number | null;
		teamStructure: string | number | null;
		teamBoard: string | null;
		teamManagement: string | null;
	};
	options: {
		motives: Array<{ value: ExtraTripMotive; label: string }>;
		categories: Array<{ value: AbsenteeismCategory; label: string }>;
		employees: Array<{ value: number; label: string }>;
		teamStructures: Array<{ value: number; label: string }>;
		teams: Array<{ value: number; label: string }>;
	};
	onMotiveChange: (value: number | string) => void;
	onDetailChange: (value: string) => void;
	onCategoryChange: (value: number | string) => void;
	onCoveredEmployeeChange: (value: number | string) => void;
	onVacancyChange: (value: string) => void;
	onTeamChange: (value: number | string) => void;
	onTeamStructureChange: (value: number | string) => void;
}

const ExtraTripFormFields: React.FC<ExtraTripFormFieldsProps> = ({
	formValues,
	options,
	onMotiveChange,
	onDetailChange,
	onCategoryChange,
	onCoveredEmployeeChange,
	onVacancyChange,
	onTeamChange,
	onTeamStructureChange,
}) => {
	const {
		motive,
		detail,
		category,
		coveredEmployee,
		coveredEmployeeStructure,
		coveredEmployeeTeam,
		vacancy,
		team,
		teamStructure,
		teamBoard,
		teamManagement,
	} = formValues;

	const { motives, categories, employees, teamStructures, teams } = options;
	const [searchTerm, setSearchTerm] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const selectedEmployee = employees.find(
		(emp) => emp.value === coveredEmployee,
	);

	const filteredEmployees = employees.filter((emp) => {
		if (!searchTerm.trim()) return true;
		return String(emp.label).toLowerCase().includes(searchTerm.toLowerCase());
	});

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node) &&
				inputRef.current &&
				!inputRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleSelectEmployee = (employee: { value: number; label: string }) => {
		onCoveredEmployeeChange(employee.value);
		setSearchTerm('');
		setIsOpen(false);
	};
	if (motive === ExtraTripMotive.SpecialServices) {
		return (
			<div className="grid grid-cols-4 gap-4">
				<div className="w-full">
					<SimpleSelect
						title="Motivo"
						values={motives}
						value={motive}
						setState={onMotiveChange}
						width="100%"
						fontSize="12px"
						titleColor="text-[#505050]"
						fontWeight="font-normal"
					/>
				</div>
				<div className="col-span-3 w-full">
					<SimpleTextField
						title="Detalhamento"
						value={detail}
						setState={onDetailChange}
						titleClassName="mb-1 text-[12px] text-[#505050] font-normal"
						inputClassName="w-full h-8 rounded-[4px] border-[1px] border-gray-400 pl-2 text-xs text-gray-800"
						required
					/>
				</div>
			</div>
		);
	}

	if (motive === ExtraTripMotive.Absenteeism) {
		return (
			<div className="flex flex-col gap-4">
				<div className="grid grid-cols-4 gap-4">
					<div>
						<SimpleSelect
							title="Motivo"
							values={motives}
							value={motive}
							setState={onMotiveChange}
							width="100%"
							fontSize="12px"
							titleColor="#505050"
						/>
					</div>
					<div>
						<SimpleSelect
							title="Categoria"
							values={categories}
							value={category || ''}
							setState={onCategoryChange}
							width="100%"
							fontSize="12px"
							titleColor="text-[#505050]"
							fontWeight="font-normal"
							required
						/>
					</div>
					<div className="col-span-2">
						<SimpleTextField
							title="Detalhamento"
							value={detail}
							setState={onDetailChange}
							titleClassName="mb-1 text-[12px] text-[#505050] font-normal"
							inputClassName="w-full h-8 rounded-[4px] border-[1px] border-gray-400 pl-2 text-xs text-gray-800"
							required
						/>
					</div>
				</div>
				<div className="grid grid-cols-4 gap-4">
					<div className="relative">
						<div className="mb-1 text-[12px] text-[#505050] font-normal">
							Colaborador coberto
							<span className="text-red">*</span>
						</div>
						<input
							ref={inputRef}
							type="text"
							value={isOpen ? searchTerm : selectedEmployee?.label || ''}
							onChange={(e) => {
								setSearchTerm(e.target.value);
								setIsOpen(true);
							}}
							onFocus={() => {
								setIsOpen(true);
								if (selectedEmployee) {
									setSearchTerm('');
								}
							}}
							placeholder="Digite para buscar..."
							className="w-full h-8 rounded-[4px] border border-gray-400 pl-2 text-xs text-gray-800 pr-8"
						/>
						{isOpen && (
							<div
								ref={dropdownRef}
								className="absolute z-50 w-full mt-1 bg-white border border-gray-400 rounded-[4px] max-h-60 overflow-y-auto shadow-lg"
							>
								{filteredEmployees.length > 0 ? (
									filteredEmployees.map((emp) => (
										// biome-ignore lint: TODO: discuss about this
										<div
											key={emp.value}
											onClick={() => handleSelectEmployee(emp)}
											className="px-2 py-1.5 text-xs text-gray-800 hover:bg-gray-100 cursor-pointer font-graphie"
										>
											{emp.label}
										</div>
									))
								) : (
									<div className="px-2 py-1.5 text-xs text-gray-500 font-graphie">
										Nenhum colaborador encontrado
									</div>
								)}
							</div>
						)}
					</div>
					<div>
						<div className="mb-1 text-[12px] text-[#4A4A4A]">Gerência</div>
						<input
							type="text"
							value={teamManagement || ''}
							readOnly
							className="h-8 w-full rounded-[4px] border border-gray-400 bg-gray-100 pl-2 text-xs text-gray-600"
						/>
					</div>
					<div>
						<div className="mb-1 text-[12px] text-[#4A4A4A]">Estrutura</div>
						<input
							type="text"
							value={coveredEmployeeStructure}
							readOnly
							className="h-8 w-full rounded-[4px] border border-gray-400 bg-gray-100 pl-2 text-xs text-gray-600"
						/>
					</div>
					<div>
						<div className="mb-1 text-[12px] text-[#4A4A4A]">Turma</div>
						<input
							type="text"
							value={coveredEmployeeTeam}
							readOnly
							className="h-8 w-full rounded-[4px] border border-gray-400 bg-gray-100 pl-2 text-xs text-gray-600"
						/>
					</div>
				</div>
			</div>
		);
	}

	if (motive === ExtraTripMotive.VacancyCovering) {
		return (
			<div className="flex flex-col gap-4">
				<div className="grid grid-cols-4 gap-4">
					<div>
						<SimpleSelect
							title="Motivo"
							values={motives}
							value={motive}
							setState={onMotiveChange}
							width="100%"
							fontSize="12px"
							titleColor="#505050"
						/>
					</div>
					<div className="col-span-3">
						<SimpleTextField
							title="Vaga"
							value={vacancy || ''}
							setState={onVacancyChange}
							titleClassName="mb-1 text-[12px] text-[#505050] font-normal"
							inputClassName="w-full h-8 rounded-[4px] border-[1px] border-gray-400 pl-2 text-xs text-gray-800"
							required
						/>
					</div>
				</div>
				<div className="grid grid-cols-4 gap-4">
					<div>
						<div className="mb-1 text-[12px] text-[#4A4A4A]">Diretoria</div>
						<input
							type="text"
							value={teamBoard || ''}
							readOnly
							className="h-8 w-full rounded-[4px] border border-gray-400 bg-gray-100 pl-2 text-xs text-gray-600"
						/>
					</div>
					<div>
						<div className="mb-1 text-[12px] text-[#4A4A4A]">Gerência</div>
						<input
							type="text"
							value={teamManagement || ''}
							readOnly
							className="h-8 w-full rounded-[4px] border border-gray-400 bg-gray-100 pl-2 text-xs text-gray-600"
						/>
					</div>
					<div>
						<SimpleSelect
							title="Estrutura"
							values={teamStructures}
							value={teamStructure || ''}
							setState={onTeamStructureChange}
							width="100%"
							fontSize="12px"
							titleColor="text-[#505050]"
							fontWeight="font-normal"
							required
						/>
					</div>
					<div>
						<SimpleSelect
							title="Turma"
							values={teams}
							value={team || ''}
							setState={onTeamChange}
							width="100%"
							fontSize="12px"
							titleColor="text-[#505050]"
							fontWeight="font-normal"
							required
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full">
			<SimpleSelect
				title="Motivo"
				values={motives}
				value={motive}
				setState={onMotiveChange}
				width="100%"
				fontSize="12px"
				titleColor="#505050"
			/>
		</div>
	);
};

export default ExtraTripFormFields;
